import { useRef, useState } from 'react';
import { adminService } from '../../services/adminService';

const CODE_LENGTH = 4;

/**
 * Blocks its children until the user enters the My Submissions access code.
 *
 * The code is verified SERVER-SIDE (POST /api/security/verify-submissions-code);
 * it is never shipped to the browser, so it cannot be read out of the bundle or
 * DevTools. Verification is deliberately NOT remembered - the prompt appears on
 * every visit, including after navigating away and back.
 */
export default function SubmissionsAccessGate({ children }) {
  const [verified, setVerified] = useState(false);
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  // Bumped on each failure so React remounts the row and the shake replays;
  // without this a second wrong code would not animate again.
  const [shakeKey, setShakeKey] = useState(0);
  const inputsRef = useRef([]);

  const focusBox = (i) => {
    const el = inputsRef.current[i];
    if (el) el.focus();
  };

  const handleChange = (index, value) => {
    // One character per box; digits only.
    const char = value.replace(/[^0-9]/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = char;
      return next;
    });
    if (error) setError('');
    if (char && index < CODE_LENGTH - 1) focusBox(index + 1);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      focusBox(index - 1);
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  // Pasting the whole code should fill every box, not just the first.
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill('');
    pasted.split('').forEach((c, i) => { next[i] = c; });
    setDigits(next);
    if (error) setError('');
    focusBox(Math.min(pasted.length, CODE_LENGTH - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (checking) return;

    const entered = digits.join('');
    if (entered.length < CODE_LENGTH) {
      setError('Please enter the security code.');
      setShakeKey((k) => k + 1);
      return;
    }

    setChecking(true);
    setError('');
    try {
      await adminService.verifySubmissionsCode(entered);
      setVerified(true);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Unable to verify the security code. Please try again.'
      );
      setDigits(Array(CODE_LENGTH).fill(''));
      setShakeKey((k) => k + 1);
      focusBox(0);
    } finally {
      setChecking(false);
    }
  };

  if (verified) return children;

  return (
    <div style={styles.overlay}>
      <form className="sec-gate-card" style={styles.card} onSubmit={handleSubmit} autoComplete="off">
        <div className="sec-gate-icon" style={styles.iconWrap} aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
               stroke="var(--company-primary-color, #273947)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h2 style={styles.title}>Security Code Required</h2>
        <p style={styles.subtitle}>
          Enter the security code to view My Submissions.
        </p>

        {/* One box per character, matching the verification-code screen patients
            see. type="text" (not password) keeps Chrome's password manager from
            offering to save the code on every submit. */}
        <div style={styles.boxRow} className={error ? "sec-gate-row-error" : undefined} key={shakeKey}>
          {Array.from({ length: CODE_LENGTH }).map((_, i) => (
            <input
              key={i}
              ref={(el) => { inputsRef.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digits[i]}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={i === 0 ? handlePaste : undefined}
              onFocus={(e) => e.target.select()}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
              autoFocus={i === 0}
              disabled={checking}
              aria-label={`Security code digit ${i + 1}`}
              className="sec-gate-box"
              placeholder=" "
              style={{
                ...styles.box,
                ...(error ? styles.boxError : {}),
                // Drives the staggered entrance in CSS.
                '--i': i,
              }}
            />
          ))}
        </div>

        {error && (
          <div className="sec-gate-error" style={styles.error} role="alert">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <button type="submit" className="sec-gate-btn" style={styles.button} disabled={checking}>
          {checking ? 'Verifying...' : 'Continue'}
        </button>
      </form>
    </div>
  );
}

const styles = {
  overlay: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  card: {
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
    padding: '32px',
    width: '100%',
    maxWidth: '380px',
    textAlign: 'center',
  },
  iconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: '#e8f4f8',
    margin: '0 auto 16px',
  },
  title: {
    margin: '0 0 8px',
    fontSize: '18px',
    fontWeight: 600,
    color: 'var(--company-primary-color, #273947)',
  },
  subtitle: {
    margin: '0 0 20px',
    fontSize: '14px',
    color: '#666',
    lineHeight: 1.5,
  },
  boxRow: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
  },
  box: {
    width: '45px',
    height: '50px',
    textAlign: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  boxError: {
    borderColor: '#e5a3a3',
  },
  // Plain inline message - no box. A filled panel drew more attention than the
  // input the user has to correct, and read as a page-level failure rather than
  // simple validation feedback.
  error: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '12px',
    color: '#b42318',
    fontSize: '13px',
    lineHeight: 1.4,
  },
  button: {
    marginTop: '18px',
    width: '100%',
    padding: '10px 16px',
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--company-primary-contrast, #fff)',
    background: 'var(--company-primary-color, #273947)',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};
